import { UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { HBars } from "@/components/charts/HBars";
import { PageHeaderBar, PageHeaderMetric } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { ClientPartnerBookDialog } from "@/components/profitability/ClientPartnerBookDialog";
import { MarginBadge } from "@/components/profitability/MarginBadge";
import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HeroPanel } from "@/components/shared/hero-panel";
import { StatusChip } from "@/components/shared/status-chip";
import { Card } from "@/components/ui/card";
import { useClientPartners } from "@/hooks/use-client-partners";
import { useClientSort } from "@/hooks/use-client-sort";
import { useRangeParam } from "@/hooks/use-range-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money, percent } from "@/lib/format";
import type { ClientPartnerRow } from "@/lib/profitability-api";

const EMPTY_ROWS: ClientPartnerRow[] = [];

export function ClientPartnersPage() {
  const [range, setRange] = useRangeParam();
  const { data, isLoading, isError, error, refetch } = useClientPartners(range);
  const [focusPartner, setFocusPartner] = useState<string | null>(null);

  const rows = data?.rows ?? EMPTY_ROWS;
  // Sorted in the browser, unlike the account and project grids: there are
  // eight rows, the whole set is already here, and a round trip to reorder
  // eight things would be slower as well as more complicated.
  // The accessor names every sortable column; useClientSort has no idea what
  // a ClientPartnerRow is.
  const sorted = useClientSort(
    rows,
    (row, key) => row[key as keyof ClientPartnerRow] as string | number | null,
    { key: "revenue", dir: "desc" },
  );
  const totals = data?.totals;

  // Assigned partners only: the Unassigned row is a remainder, not a book
  // anyone carries, so it has no place in a comparison of books.
  const assigned = useMemo(() => rows.filter((row) => !row.is_unassigned), [rows]);
  const bestBook = useMemo(() => {
    let best: ClientPartnerRow | null = null;
    for (const row of assigned) {
      if (row.margin_pct === null) continue;
      if (best === null || row.margin_pct > (best.margin_pct ?? Number.NEGATIVE_INFINITY)) {
        best = row;
      }
    }
    return best;
  }, [assigned]);
  const contested = rows.reduce((sum, row) => sum + row.contested_accounts, 0);

  const partner = useMemo(
    () => rows.find((row) => (row.person_id ?? row.person_name) === focusPartner) ?? null,
    [rows, focusPartner],
  );

  const rangeInfo = data?.range;
  // Published before the error branch, so a failed load still shows the title
  // and the range that produced it rather than an unlabelled error.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="Client Partner profitability"
        fields={[
          // The board total, in the same labelled slot as every other page's
          // headline figure. The partner COUNT that used to lead this line is
          // gone: the table below is the count.
          totals
            ? {
                key: "revenue",
                label: "Revenue",
                content: (
                  <PageHeaderMetric
                    value={money(totals.revenue)}
                    badge={<MarginBadge value={totals.margin_pct} benchmark={null} />}
                  />
                ),
              }
            : null,
        ]}
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [totals, rangeInfo, range, setRange],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {totals ? (
        <HeroPanel
          eyebrow={`Carried by ${totals.client_partners} Client Partner${totals.client_partners === 1 ? "" : "s"}`}
          figure={money(totals.revenue)}
          chart={
            assigned.length >= 2 ? (
              <HBars
                label="Margin by Client Partner, largest books first"
                height={Math.min(assigned.length, 6) * 22 + 12}
                rows={assigned.slice(0, 6).map((row) => ({
                  label: row.person_name,
                  value: row.margin_pct ?? 0,
                  display: percent(row.margin_pct),
                }))}
              />
            ) : undefined
          }
          facts={[
            ...(bestBook
              ? [
                  {
                    label: "Best book",
                    value: `${bestBook.person_name.split(" ")[0]} \u00b7 ${percent(bestBook.margin_pct)}`,
                  },
                ]
              : []),
            ...(contested > 0
              ? [{ label: "Contested", value: `${contested} account${contested === 1 ? "" : "s"}` }]
              : []),
          ]}
        />
      ) : null}

      <Card flush className="w-full">
        <DataTable
          flush
          dense
          loading={isLoading}
          sort={sorted.sort}
          sortDir={sorted.sortDir}
          onSort={sorted.toggleSort}
          headers={[
            {
              label: "Client Partner",
              sortKey: "person_name",
              // The inference sentence lives here as hover text now, not under
              // the hero figure: the people reading this page know the role.
              help: data?.basis
                ? `${COLUMN_HELP.clientPartner} ${data.basis}`
                : COLUMN_HELP.clientPartner,
            },
            { label: "Accounts", sortKey: "accounts", className: "text-right" },
            {
              label: "Margin",
              sortKey: "margin_pct",
              className: "text-right",
              help: COLUMN_HELP.margin,
            },
            {
              label: "Below target",
              sortKey: "gap_vs_benchmark",
              className: "text-right",
              help: COLUMN_HELP.belowTarget,
            },
            {
              label: "Revenue",
              sortKey: "revenue",
              className: "text-right",
              help: COLUMN_HELP.revenue,
            },
            { label: "Cost", sortKey: "cost", className: "text-right", help: COLUMN_HELP.cost },
            { label: "Profit", sortKey: "profit", className: "text-right" },
            { label: "Hours", sortKey: "hours", className: "text-right", help: COLUMN_HELP.hours },
          ]}
        >
          {sorted.rows.map((row) => {
            const key = row.person_id ?? row.person_name;
            const muted = row.is_unassigned ? " text-muted-foreground" : "";
            return (
              <tr key={key} className={ROW_CLASS}>
                <td className={`px-4 py-2.5 font-medium${muted}`}>
                  {/* A real button, so the drill-in is reachable by keyboard and
                      not only by pointer. */}
                  <button
                    type="button"
                    className="text-left hover:underline"
                    onClick={() => setFocusPartner(key)}
                    title={`See the accounts ${row.person_name} carries`}
                  >
                    {row.person_name}
                  </button>
                  {row.is_unassigned ? (
                    <StatusChip
                      tone="neutral"
                      className="ml-2"
                      title="Accounts no Client Partner has booked time to. Kept as a row so these figures still add up to the company total."
                    >
                      no owner
                    </StatusChip>
                  ) : null}
                  {row.contested_accounts > 0 ? (
                    <StatusChip
                      tone="neutral"
                      className="ml-2"
                      title={`${row.contested_accounts} of these accounts has another Client Partner with comparable hours, so its owner is not settled.`}
                    >
                      {row.contested_accounts} contested
                    </StatusChip>
                  ) : null}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>{row.accounts}</td>
                <td className="px-4 py-2.5 text-right">
                  <MarginBadge value={row.margin_pct} benchmark={data?.benchmark_pct ?? null} />
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>
                  {row.gap_vs_benchmark === null ? "--" : money(row.gap_vs_benchmark)}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>
                  {money(row.revenue)}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>{money(row.cost)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>
                  {money(row.profit)}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums${muted}`}>
                  {formatHours(row.hours)}
                </td>
              </tr>
            );
          })}
          {totals && rows.length > 0 ? (
            <tr key="company-total" className={TOTAL_ROW_CLASS}>
              <td className="px-4 py-2.5">Company</td>
              <td className="px-4 py-2.5" />
              <td className="px-4 py-2.5 text-right">
                <MarginBadge value={totals.margin_pct} benchmark={data?.benchmark_pct ?? null} />
              </td>
              <td className="px-4 py-2.5" />
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.revenue)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.cost)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(totals.profit)}</td>
              <td className="px-4 py-2.5" />
            </tr>
          ) : null}
        </DataTable>
      </Card>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          icon={UserRound}
          title="No Client Partners in this range"
          description="Nobody with the Client Partner role booked time to an account in the selected months."
        />
      ) : null}

      <ClientPartnerBookDialog
        partner={partner}
        range={range}
        onOpenChange={(open) => {
          if (!open) setFocusPartner(null);
        }}
      />
    </div>
  );
}
